
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

export function useHiringNavigation() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check if user is authenticated and has a business profile
  useEffect(() => {
    const checkUser = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to start the hiring process.",
          variant: "destructive",
        });
        navigate("/auth");
      } else {
        const { data: businessData, error } = await supabase
          .from('business')
          .select('*')
          .eq('owner_id', data.session.user.id)
          .maybeSingle();
        
        if (!businessData) {
          navigate("/onboarding");
        } else if (!businessData.is_onboarding_complete) {
          // Business exists but onboarding is not complete
          toast({
            title: "Onboarding Required",
            description: "Please complete the onboarding process first.",
          });
          navigate("/onboarding");
        }
      }
    };
    
    checkUser();
  }, [navigate, toast]);

  const handleComplete = async () => {
    try {
      // Mark onboarding as complete if there's a partial business record
      const { data: session } = await supabase.auth.getSession();
      
      if (session?.session) {
        // Get the business record
        const { data: businessData } = await supabase
          .from('business')
          .select('id, is_onboarding_complete')
          .eq('owner_id', session.session.user.id)
          .single();
        
        if (businessData && !businessData.is_onboarding_complete) {
          // Update the onboarding status
          await supabase
            .from('business')
            .update({ is_onboarding_complete: true })
            .eq('id', businessData.id);
        }
      }
      
      // Show success toast
      toast({
        title: "Onboarding Complete!",
        description: "Your AI Assistant is ready to help you grow your business.",
      });
      
      // Invalidate queries to ensure fresh data when redirected to dashboard
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['allStaff'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
      
      // Navigate to dashboard
      navigate("/app/dashboard");
    } catch (error) {
      console.error("Error completing workflow:", error);
      toast({
        title: "Error",
        description: "An error occurred while completing setup.",
        variant: "destructive",
      });
    }
  };

  return {
    navigate,
    handleComplete
  };
}
